import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { confirmUserAttribute } from "@aws-amplify/auth";

interface EmailVerificationModalProps {
  open: boolean;
  onClose: () => void;
  email: string; // The email address to verify
  onVerified: () => void;
  disableClose?: boolean; // Prevent closing modal (for required verification)
  isEmailChange?: boolean; // True if verifying email change, false if verifying initial signup
}

/**
 * Modal for verifying email via 6-digit confirmation code
 * Supports both initial signup verification and email change verification
 */
export const EmailVerificationModal: React.FC<EmailVerificationModalProps> = ({
  open,
  onClose,
  email,
  onVerified,
  disableClose = false,
  isEmailChange = true,
}) => {
  const [verificationCode, setVerificationCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState("");

  const handleVerify = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      setError("Please enter a valid 6-digit code");
      return;
    }

    setLoading(true);
    setError(null);
    setSuccessMessage("");

    try {
      if (isEmailChange) {
        // Verify the email attribute change with Cognito
        await confirmUserAttribute({
          userAttributeKey: "email",
          confirmationCode: verificationCode,
        });
      } else {
        // Verify initial signup email
        const { confirmSignUp } = await import("@aws-amplify/auth");
        await confirmSignUp({
          username: email,
          confirmationCode: verificationCode,
        });
      }

      setSuccessMessage("Email verified successfully!");

      // Wait a moment to show success message, then notify parent
      setTimeout(() => {
        onVerified();
        // Only close if dismissible (parent will handle closing for non-dismissible modals)
        if (!disableClose) {
          handleClose();
        }
      }, 1500);
    } catch (err: any) {
      console.error("Email verification error:", err);
      setError(
        err.message ||
          "Failed to verify email. Please check your code and try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setError(null);
    setSuccessMessage("");

    try {
      if (isEmailChange) {
        // Request a new verification code for the email attribute
        const { updateUserAttribute } = await import("@aws-amplify/auth");
        await updateUserAttribute({
          userAttribute: {
            attributeKey: "email",
            value: email,
          },
        });
        setSuccessMessage(
          "Verification code resent to your new email address.",
        );
      } else {
        // Resend signup confirmation code
        const { resendSignUpCode } = await import("@aws-amplify/auth");
        await resendSignUpCode({ username: email });
        setSuccessMessage("Verification code resent to your email address.");
      }
    } catch (err: any) {
      console.error("Resend code error:", err);
      setError(err.message || "Failed to resend verification code");
    }
  };

  const handleClose = () => {
    setVerificationCode("");
    setError(null);
    setSuccessMessage("");
    onClose();
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && verificationCode.length === 6 && !loading) {
      handleVerify();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={disableClose ? undefined : handleClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={disableClose}
    >
      <DialogTitle sx={{ color: "primary.main" }}>
        {isEmailChange ? "Verify Your New Email" : "Verify Your Email"}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" paragraph>
          We've sent a 6-digit verification code to:
        </Typography>
        <Typography variant="body1" fontWeight="medium" paragraph>
          {email}
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          {isEmailChange
            ? "Please enter the code below to confirm your new email address."
            : "Please enter the code below to verify your account and continue."}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {successMessage && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {successMessage}
          </Alert>
        )}

        <TextField
          fullWidth
          label="Verification Code"
          value={verificationCode}
          onChange={(e) => {
            // Only allow digits and limit to 6 characters
            const value = e.target.value.replace(/\D/g, "").slice(0, 6);
            setVerificationCode(value);
          }}
          onKeyPress={handleKeyPress}
          placeholder="Enter 6-digit code"
          disabled={loading}
          autoFocus
          inputProps={{
            maxLength: 6,
            inputMode: "numeric",
            pattern: "[0-9]*",
          }}
          sx={{ mt: 2 }}
          helperText="Enter the 6-digit code from your email"
        />

        <Box sx={{ mt: 2 }}>
          <Button
            variant="text"
            size="small"
            onClick={handleResendCode}
            disabled={loading}
          >
            Didn't receive the code? Resend
          </Button>
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 3, pt: 0 }}>
        {!disableClose && (
          <Button onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
        )}
        <Button
          variant="contained"
          onClick={handleVerify}
          disabled={loading || verificationCode.length !== 6}
        >
          {loading ? <CircularProgress size={24} /> : "Verify Email"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
